import { m } from "@recobra/i18n/messages";

/** Same order as the sidebar user label: name → login id → email → username. */
export const resolveUserDisplayName = (input: {
  username: string;
  name?: string;
  loginId?: string;
  email?: string;
}): string => {
  const name = input.name?.trim();
  if (name) return name;

  const loginId = input.loginId?.trim();
  if (loginId) return loginId;

  const email = input.email?.trim();
  if (email) return email;

  return input.username.trim();
};

/** Text for a user whose attributes are already at hand — never a raw id. */
export const userDisplayText = (user: { name?: string; email?: string } | undefined): string =>
  user?.name?.trim() || user?.email?.trim() || m.user_unknown();

export const sortUsersByDisplayName = <T extends UserWithDisplayName>(users: readonly T[]): T[] =>
  users.toSorted((left, right) =>
    userDisplayNameCollator.compare(userDisplayName(left), userDisplayName(right)),
  );

const userDisplayNameCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

type UserWithDisplayName = {
  username: string;
  name?: string;
  email?: string;
  attributes?: {
    name?: string;
    email?: string;
  };
};

const userDisplayName = (user: UserWithDisplayName): string =>
  resolveUserDisplayName({
    username: user.username,
    name: user.name ?? user.attributes?.name,
    email: user.email ?? user.attributes?.email,
  });

const COGNITO_SUB_PATTERN = /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

/**
 * Human accounts carry their sub as the Cognito username, so only service
 * principals (e.g. the automation token) have one worth showing.
 */
export const namedPrincipal = (username: string): string | undefined =>
  COGNITO_SUB_PATTERN.test(username) ? undefined : username;

export const getInitials = (name: string): string =>
  name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
